import { useCallback, useRef } from "react";

const MAX_HISTORY = 50;

const cloneSnapshot = (snapshot) => structuredClone(snapshot);

export const useTemplateEditorHistory = ({ getSnapshot, applySnapshot }) => {
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const lastCoalesceKeyRef = useRef(null);
  const getSnapshotRef = useRef(getSnapshot);
  const applySnapshotRef = useRef(applySnapshot);
  getSnapshotRef.current = getSnapshot;
  applySnapshotRef.current = applySnapshot;

  const push = useCallback(({ coalesceKey } = {}) => {
    if (coalesceKey && lastCoalesceKeyRef.current === coalesceKey) return;
    pastRef.current.push(cloneSnapshot(getSnapshotRef.current()));
    if (pastRef.current.length > MAX_HISTORY) {
      pastRef.current.shift();
    }
    futureRef.current = [];
    lastCoalesceKeyRef.current = coalesceKey ?? null;
  }, []);

  const endCoalesce = useCallback(() => {
    lastCoalesceKeyRef.current = null;
  }, []);

  const undo = useCallback(() => {
    if (!pastRef.current.length) return false;
    const current = cloneSnapshot(getSnapshotRef.current());
    const previous = pastRef.current.pop();
    futureRef.current.push(current);
    lastCoalesceKeyRef.current = null;
    applySnapshotRef.current(cloneSnapshot(previous));
    return true;
  }, []);

  const redo = useCallback(() => {
    if (!futureRef.current.length) return false;
    const current = cloneSnapshot(getSnapshotRef.current());
    const next = futureRef.current.pop();
    pastRef.current.push(current);
    lastCoalesceKeyRef.current = null;
    applySnapshotRef.current(cloneSnapshot(next));
    return true;
  }, []);

  const clear = useCallback(() => {
    pastRef.current = [];
    futureRef.current = [];
    lastCoalesceKeyRef.current = null;
  }, []);

  return { push, endCoalesce, undo, redo, clear };
};
